/**
 * Performance API Helper Functions
 * 
 * Safe wrappers around the browser Performance API to prevent errors
 * from missing marks or unavailable API features.
 */

/**
 * Checks if a performance mark exists
 * @param markName - The name of the mark to check
 * @returns true if the mark exists, false otherwise
 */
export function hasPerformanceMark(markName: string): boolean {
  if (typeof performance === 'undefined' || !performance.getEntriesByName) {
    return false;
  }
  return performance.getEntriesByName(markName, 'mark').length > 0;
}

/**
 * Safely creates a performance mark
 * @param markName - The name of the mark to create
 */
export function safeMarkPerformance(markName: string): void {
  try {
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(markName);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Failed to create performance mark "${markName}":`, error);
    }
  }
}

/**
 * Safely measures performance between two marks
 * @param measureName - The name for this measurement
 * @param startMark - The name of the start mark
 * @param endMark - The name of the end mark
 * @param onError - Optional error callback
 * @returns The performance measure entry, or null if measurement failed
 */
export function safeMeasurePerformance(
  measureName: string,
  startMark: string,
  endMark: string,
  onError?: (error: Error) => void
): PerformanceMeasure | null {
  try {
    // Check if Performance API is available
    if (typeof performance === 'undefined' || !performance.measure) {
      return null;
    }

    // Verify both marks exist
    if (!hasPerformanceMark(startMark)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Start mark "${startMark}" not found for measurement "${measureName}"`);
      }
      return null;
    }

    if (!hasPerformanceMark(endMark)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`End mark "${endMark}" not found for measurement "${measureName}"`);
      }
      return null;
    }

    // Create the measurement
    performance.measure(measureName, startMark, endMark);
    
    // Retrieve and return the measurement
    const measures = performance.getEntriesByName(measureName, 'measure');
    return measures[measures.length - 1] as PerformanceMeasure;
    
  } catch (error) {
    const err = error as Error;
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Failed to measure performance "${measureName}":`, err);
    }
    onError?.(err);
    return null;
  }
}

/**
 * Clears performance marks to prevent memory buildup
 * @param markNames - Names of marks to clear
 */
export function clearPerformanceMarks(...markNames: string[]): void {
  try {
    if (typeof performance !== 'undefined' && performance.clearMarks) {
      markNames.forEach(markName => {
        performance.clearMarks(markName);
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to clear performance marks:', error);
    }
  }
}

/**
 * Clears performance measures
 * @param measureNames - Names of measures to clear
 */
export function clearPerformanceMeasures(...measureNames: string[]): void {
  try {
    if (typeof performance !== 'undefined' && performance.clearMeasures) {
      measureNames.forEach(measureName => {
        performance.clearMeasures(measureName);
      });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to clear performance measures:', error);
    }
  }
}
